import React, { useEffect, useState, useRef, useContext } from 'react';
import DataService from '../../XXX/DataService';
import GameAssetsContext from '../../XXX/context/GameAssetContext';
import ComboDisplay from '../../XXX/GameSimulator/GameContainer2/GameGrid/ChampionCard/ComboDisplay/ComboDisplay';
import { championImages } from '../../XXX/GameSimulator/GameContainer2/GameGrid/ChampionCard/ComboDisplay/Events/Images/championImages';
import '../../NewWebSocket/components/SimpleMatchupDisplay.css';

interface ComboData {
  combo: any;
  damage: number;
  targetMaxHealth: number;
  events: any[];
  isSingleAbility: boolean;
  cached?: boolean;
  simulated?: boolean;
  error?: boolean;
}

interface MatchupCombos {
  mainChampion: {
    name: string;
    level: number;
    combos: ComboData[];
  };
  opponent: {
    name: string;
    level: number;
    combos: ComboData[];
  };
  summary?: {
    total: number;
    cached: number;
    simulated: number;
    uncached: number;
  };
  availableSkills?: string[];
}

interface StaticSimpleMatchupDisplayProps {
  championId: string;
  targetChampionId: string | null;
  championName: string;
  targetChampionName?: string;
}

const StaticSimpleMatchupDisplay: React.FC<StaticSimpleMatchupDisplayProps> = ({ 
  championId, 
  targetChampionId, 
  championName, 
  targetChampionName 
}) => {
  const [matchupCombos, setMatchupCombos] = useState<MatchupCombos | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedCombos, setExpandedCombos] = useState<Record<number, boolean>>({});
  const [showSingleAbilities, setShowSingleAbilities] = useState(true);
  const [showMultiAbilities, setShowMultiAbilities] = useState(true);
  
  const { championsAssets, fetchAndSetChampionAssets } = useContext(GameAssetsContext);

  // Guard against duplicate API calls
  const lastKey = useRef<string | null>(null);

  // Fetch champion assets
  useEffect(() => {
    if (!championsAssets[championName]) {
      fetchAndSetChampionAssets(championName);
    }
    if (targetChampionName && !championsAssets[targetChampionName]) {
      fetchAndSetChampionAssets(targetChampionName);
    }
  }, [championName, targetChampionName, championsAssets, fetchAndSetChampionAssets]);

  const toggleExpandCombo = (index: number) => {
    setExpandedCombos(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Function to format damage display with percentage
  const formatDamage = (damage: number, targetMaxHealth?: number) => {
    // Round damage to nearest integer
    const roundedDamage = Math.round(damage);
    
    // Calculate percentage of target's max health if available
    if (targetMaxHealth && targetMaxHealth > 0) {
      const percentage = (damage / targetMaxHealth) * 100;
      return (
        <div>
          <div className="damage-value">{roundedDamage}</div>
          <div className="damage-percentage">{percentage.toFixed(1)}%</div>
        </div>
      );
    }
    
    // Fallback to just rounded damage
    return roundedDamage;
  };

  useEffect(() => {
    console.log(`[StaticSimpleMatchupDisplay] useEffect triggered!`);
    console.log(`[StaticSimpleMatchupDisplay] - targetChampionId:`, targetChampionId ? 'present' : 'null');
    
    if (!targetChampionId) {
      console.log(`[StaticSimpleMatchupDisplay] No target, clearing state`);
      setMatchupCombos(null);
      lastKey.current = null;
      return;
    }

    // Build a string key for the API call
    const key = `${championId}-vs-${targetChampionId}`;

    console.log(`[StaticSimpleMatchupDisplay] key:`, key);
    console.log(`[StaticSimpleMatchupDisplay] lastKey.current:`, lastKey.current);

    // Only fetch if that key really changed
    if (key === lastKey.current) {
      console.log(`[StaticSimpleMatchupDisplay] Key unchanged, skipping fetch`);
      return;
    }
    lastKey.current = key;

    console.log(`[StaticSimpleMatchupDisplay] Making API call for new key`);
    setLoading(true);
    setError(null);
    DataService.fetchStaticMatchupCombos(championId, targetChampionId)
      .then(data => {
        console.log(`[StaticSimpleMatchupDisplay] API call succeeded:`, data);
        setMatchupCombos(data);
      })
      .catch(err => {
        console.log(`[StaticSimpleMatchupDisplay] API call failed:`, err);
        setError(err instanceof Error ? err.message : 'Failed to fetch combos');
        lastKey.current = null;  // allow a retry
      })
      .finally(() => {
        setLoading(false);
      });
  }, [championId, targetChampionId]);

  if (!targetChampionId) {
    return (
      <div className="combo-selector">
        <div className="combo-selector-empty">
          <div>💡 Select a target champion to view combos</div>
          <p>Choose an opponent to see {championName}'s combo damage calculations.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="combo-selector">
        <div className="combo-selector-loading">
          <div>↻ Loading combos...</div>
          <p>Calculating damage for {championName} vs {targetChampionName}...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="combo-selector">
        <div className="combo-selector-error">
          <div>❌ Error loading combos</div>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!matchupCombos || !matchupCombos.mainChampion) {
    return (
      <div className="combo-selector">
        <div className="combo-selector-empty">
          <div>🔍 No combo data available</div>
          <p>No combos found for {championName} vs {targetChampionName}</p>
        </div>
      </div>
    );
  }

  const combos = matchupCombos.mainChampion.combos || [];
  const singleAbilities = combos.filter(combo => combo.isSingleAbility);
  const multiAbilities = combos.filter(combo => !combo.isSingleAbility);

  return (
    <div className="combo-selector">


      {/* Single Abilities Section */}
      {singleAbilities.length > 0 && (
        <div className="combo-section">
          <div 
            className="section-header" 
            onClick={() => setShowSingleAbilities(!showSingleAbilities)}
          >
            <h4>
              <span className="expand-icon">{showSingleAbilities ? '▼' : '►'}</span>
              Abilities ({singleAbilities.length})
            </h4>
          </div>
          
          {showSingleAbilities && (
            <div className="section-content">
              {singleAbilities.map((comboData, index) => {
                return (
                  <div key={`single-${index}`} className="combo-item">
                    <div 
                      className="combo-header"
                      onClick={() => toggleExpandCombo(index)}
                    >
                      <h4>
                        <span className="expand-icon">
                          {expandedCombos[index] ? '▼' : '►'}
                        </span>
                        <MiniAbilityIcons sequence={comboData.combo.sequence} championName={championName} events={comboData.events} />
                        <span className="combo-name">{comboData.combo.name || comboData.combo.sequence}</span>
                      </h4>
                      <span className="combo-damage">{formatDamage(comboData.damage, comboData.targetMaxHealth)}</span>
                    </div>
                  
                  {expandedCombos[index] && (
                    <div className="combo-events">
                      <ComboDisplay
                        logs={comboData.events}
                        championName={championName}
                        targetChampionName={targetChampionName}
                        targetMaxHealth={comboData.targetMaxHealth}
                        showDetails={true}
                        showStackingEvents={true}
                        showStatEvents={true}
                        showCCEvents={true}
                      />
                      
                    </div>
                  )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      
      {/* Multi-Ability Combos Section */}
      {multiAbilities.length > 0 && (
        <div className="combo-section">
          <div 
            className="section-header" 
            onClick={() => setShowMultiAbilities(!showMultiAbilities)}
          >
            <h4>
              <span className="expand-icon">{showMultiAbilities ? '▼' : '►'}</span>
              Combos ({multiAbilities.length})
            </h4>
          </div>
          
          {showMultiAbilities && (
            <div className="section-content">
              {multiAbilities.map((comboData, index) => {
                return (
                  <div key={`multi-${index}`} className="combo-item">
                    <div 
                      className="combo-header"
                      onClick={() => toggleExpandCombo(index + 1000)}
                    >
                      <h4>
                        <span className="expand-icon">
                          {expandedCombos[index + 1000] ? '▼' : '►'}
                        </span>
                        <MiniAbilityIcons sequence={comboData.combo.sequence} championName={championName} events={comboData.events} />
                        {/* No combo name for multi-ability combos */}
                      </h4>
                      <span className="combo-damage">{formatDamage(comboData.damage, comboData.targetMaxHealth)}</span>
                    </div>
                  
                  {expandedCombos[index + 1000] && (
                    <div className="combo-events">
                      {comboData.combo.description && (
                        <div className="combo-description">
                          {comboData.combo.description}
                        </div>
                      )}
                      
                      <ComboDisplay
                        logs={comboData.events}
                        championName={championName}
                        targetChampionName={targetChampionName}
                        targetMaxHealth={comboData.targetMaxHealth}
                        showDetails={true}
                        showStackingEvents={true}
                        showStatEvents={true}
                        showCCEvents={true}
                      />
                      
                    </div>
                  )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Helper function to extract base ability from duration-enhanced sequences
const extractBaseAbility = (skill: string): string => {
  // Handle wait/delay notation (_:1.5 or just _)
  if (skill.startsWith('_')) {
    return '_';
  }
  
  // Handle cast time break (T:0.5)
  if (skill.startsWith('T:')) {
    return 'T';
  }
  
  // Handle blocking hold notation (E[3], Q[max], R[min])
  const blockingHoldMatch = skill.match(/^([QWERA])\[/);
  if (blockingHoldMatch) {
    return blockingHoldMatch[1];
  }
  
  // Handle non-blocking toggle notation (R::4, Q::max)
  const nonBlockingToggleMatch = skill.match(/^([QWERA])::/);
  if (nonBlockingToggleMatch) {
    return nonBlockingToggleMatch[1];
  }
  
  // Handle explicit recast format (E:3E, Q:2Q, etc.) — legacy
  const explicitRecastMatch = skill.match(/^([QWERA]):(\d+\.?\d*|\w+)\1$/);
  if (explicitRecastMatch) {
    return explicitRecastMatch[1];
  }
  
  // Handle duration format (E:3, Q:max, E:min, etc.) — legacy
  if (skill.includes(':')) {
    const [ability] = skill.split(':');
    if (['Q', 'W', 'E', 'R', 'A'].includes(ability)) {
      return ability;
    }
  }
  
  // Return as-is if no duration syntax found
  return skill;
};

// Helper function to extract KeyPress events from logs in sequence order
const extractKeyPressEventsFromLogs = (events: any[]): Array<{ability: string, imageState: string, championName: string}> => {
  if (!events || events.length === 0) return [];
  
  const keyPressEvents: Array<{ability: string, imageState: string, championName: string}> = [];
  
  // Recursive function to traverse event tree and collect KeyPress events
  const traverseEvents = (eventList: any[]) => {
    eventList.forEach(event => {
      if (event.type === 'KeyPress') {
        const abilityKey = event.details?.abilityKey?.toUpperCase() || '';
        const imageState = event.imageState || 'default';
        const championName = event.details?.championName || '';
        
        if (abilityKey) {
          keyPressEvents.push({
            ability: abilityKey,
            imageState: imageState,
            championName: championName
          });
        }
      }
      
      // Recursively check child events
      if (event.childEvents && event.childEvents.length > 0) {
        traverseEvents(event.childEvents);
      }
    });
  };
  
  traverseEvents(events);
  return keyPressEvents;
};

// Mini component to display ability icons in a sequence using actual KeyPress events from logs
const MiniAbilityIcons: React.FC<{
  sequence: string;
  championName: string;
  events?: any[]; // Optional events array from combo logs
}> = ({ sequence, championName, events }) => {
  const { championsAssets } = useContext(GameAssetsContext);
  
  // Try to extract KeyPress events from logs first
  const keyPressEvents = events ? extractKeyPressEventsFromLogs(events) : [];
  
  // Fallback: Extract individual abilities from sequence, handling duration syntax
  // Filter out wait/delay actions (_) — they don't have icons
  const fallbackAbilities = sequence
    .split(/[,\s]/)
    .filter(Boolean)
    .map(ability => {
      const trimmed = ability.trim().toUpperCase();
      // Extract base ability from duration-enhanced sequences (E:2E -> E, Q:max -> Q, etc.)
      return extractBaseAbility(trimmed);
    })
    .filter(ability => ability !== '_' && ability !== 'T');

  // Return if assets aren't loaded yet
  if (!championsAssets[championName]) {
    return <span className="mini-sequence-text">{sequence}</span>;
  }
  
  const spellImageUrls = championsAssets[championName].spellImageUrls;
  const autoAttackImageUrl = championsAssets[championName].autoAttackImageUrl;
  
  // Use KeyPress events if available, otherwise fallback to sequence parsing
  const abilitiesToRender = keyPressEvents.length > 0 ? keyPressEvents : fallbackAbilities.map(ability => ({
    ability,
    imageState: 'default',
    championName
  }));
  
  return (
    <div className="mini-ability-sequence">
      {abilitiesToRender.map((abilityData, idx) => {
        const ability = typeof abilityData === 'string' ? abilityData : abilityData.ability;
        const imageState = typeof abilityData === 'string' ? 'default' : abilityData.imageState;
        let iconUrl = '';
        
        // For abilities (not auto attacks), try to use championImages with imageState
        if (ability !== 'A' && championName && championImages[championName]) {
          const champMap = championImages[championName];
          
          // 1) If imageState != "default", try championImages[championName][imageState]
          if (imageState && imageState !== "default") {
            iconUrl = champMap[imageState.toLowerCase()];
          }
          
          // 2) If still no icon, or imageState was "default", try champMap[abilityKey]
          if (!iconUrl) {
            iconUrl = champMap[ability.toLowerCase()];
          }
        }
        
        // Fallback to base spell images if championImages didn't provide an icon
        if (!iconUrl) {
          switch (ability) {
            case 'Q':
              iconUrl = spellImageUrls.Q;
              break;
            case 'W':
              iconUrl = spellImageUrls.W;
              break;
            case 'E':
              iconUrl = spellImageUrls.E;
              break;
            case 'R':
              iconUrl = spellImageUrls.R;
              break;
            case 'A':
              iconUrl = autoAttackImageUrl;
              break;
            default:
              return <span key={`ability-${idx}`} className="unknown-ability">{ability}</span>;
          }
        }
        
        return (
          <React.Fragment key={`ability-fragment-${idx}`}>
            <div className="mini-ability-icon">
              <img src={iconUrl} alt={ability} />
              <span className="mini-ability-letter">{ability}</span>
            </div>
            {/* Add arrow between icons, but not after the last one */}
            {idx < abilitiesToRender.length - 1 && <span className="ability-arrow">→</span>}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default StaticSimpleMatchupDisplay; 